// Выход заготовки: сырьё (брутто) → потери при обработке → готовый продукт (нетто).
//
// На бэке выход хранится «до потерь» (outputQuantity) вместе с процентом потерь,
// а нетто = outputQuantity × (1 − потери). Здесь outputQuantity — это сумма сырья:
// 3,15 кг граната при потерях 70% дают 0,945 л сока, 1 л стоит 882 ₽ / 0,945.
import { CircleHelp, Database, Droplet, GlassWater, Info, Leaf, Scale } from 'lucide-react';

import { NumberInput } from '@/components/ui/NumberInput';
import { formatRub } from '@/utils/format';
import { QuantityField } from './QuantityField';

export type YieldMode = 'output' | 'raw';

/** Как на бэке: больше — почти наверняка опечатка, 100% дало бы деление на ноль. */
export const MAX_LOSS_PERCENT = 95;

/**
 * Масса и объём в кг/л. Выход на кухне считают по весу: 1 кг сырья ≈ 1 л продукта
 * до потерь, поэтому кг и л складываются один к одному. null — штуки, упаковки и
 * прочее, что в сумму сырья не входит.
 */
export function bulkFactor(unit?: string | null): number | null {
  switch ((unit ?? '').trim().toLowerCase()) {
    case 'кг':
    case 'л':
      return 1;
    case 'г':
    case 'мл':
      return 0.001;
    default:
      return null;
  }
}

/** Сумма сырья в кг/л по строкам с массой или объёмом. */
export function sumRaw(rows: Array<{ quantity: number; unit?: string | null }>): number {
  return rows.reduce((s, r) => {
    const f = bulkFactor(r.unit);
    return f == null ? s : s + r.quantity * f;
  }, 0);
}

/** Единица суммы сырья: литры, только если всё сырьё жидкое. */
export function rawUnitOf(units: Array<string | null | undefined>): 'кг' | 'л' {
  const bulk = units.map((u) => (u ?? '').trim().toLowerCase()).filter((u) => bulkFactor(u) != null);
  return bulk.length > 0 && bulk.every((u) => u === 'л' || u === 'мл') ? 'л' : 'кг';
}

export const round6 = (v: number) => Math.round(v * 1e6) / 1e6;

/** 3,3333 → «3,33», 0,945 → «0,945»: крупное — до сотых, мелкое — до тысячных. */
export function fmtYield(v: number): string {
  // 3,15 × 0,09 = 0,28349999… — без этого одно и то же число печаталось бы то 0,283, то 0,284.
  const r = round6(v);
  return r.toLocaleString('ru-RU', { maximumFractionDigits: Math.abs(r) >= 1 ? 2 : 3 });
}

export function fmtPercent(v: number): string {
  return round6(v).toLocaleString('ru-RU', { maximumFractionDigits: 1 });
}

// ─── Блоки редактора ─────────────────────────────────────────────────────

const MODES = [
  { id: 'output', icon: GlassWater, title: 'Расчёт по выходу', hint: 'Указать, сколько готовой заготовки нужно получить' },
  { id: 'raw', icon: Leaf, title: 'Расчёт по сырью', hint: 'Указать количество сырья и увидеть выход' },
] as const;

const MODE_ON = 'border-violet-300 bg-violet-50 text-violet-950';
const MODE_OFF = 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50';

export function YieldModeSwitch({ mode, onChange }: { mode: YieldMode; onChange: (m: YieldMode) => void }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Способ расчёта заготовки">
      {MODES.map(({ id, icon: Icon, title, hint }) => {
        const active = mode === id;
        return (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(id)}
            className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${active ? MODE_ON : MODE_OFF}`}
          >
            <Icon size={20} className={`mt-0.5 shrink-0 ${active ? 'text-violet-600' : 'text-slate-400'}`} />
            <span>
              <span className="block text-sm font-medium">{title}</span>
              <span className={`block text-xs ${active ? 'text-violet-800' : 'text-slate-500'}`}>{hint}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function YieldModeNote({ mode }: { mode: YieldMode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-950">
      <Info size={16} className="mt-0.5 shrink-0 text-indigo-500" />
      <span>
        {mode === 'output'
          ? 'Система рассчитает необходимое количество сырья с учётом потерь при обработке.'
          : 'Система рассчитает фактический выход готовой заготовки и её себестоимость по количеству сырья и потерям при обработке.'}
      </span>
    </div>
  );
}

export function YieldInputs({
  mode, prepUnit, rawUnit, net, raw, loss, lossOk, onNet, onRaw, onLoss,
}: {
  mode: YieldMode;
  prepUnit: string;
  rawUnit: string;
  net: number;
  raw: number;
  loss: number;
  lossOk: boolean;
  onNet: (v: number) => void;
  onRaw: (v: number) => void;
  onLoss: (v: number) => void;
}) {
  return (
    <div className="grid gap-3 rounded-lg border border-slate-200 bg-white px-3 py-3 sm:grid-cols-[1fr_auto]">
      <div className="grid gap-1 text-sm">
        <span className="text-slate-600">
          {mode === 'output' ? 'Желаемый выход готовой заготовки' : 'Количество сырья (брутто)'}
        </span>
        {mode === 'output'
          ? <QuantityField key="net" value={net} unit={prepUnit} onChange={onNet} startInBaseUnit />
          : <QuantityField key="raw" value={raw} unit={rawUnit} onChange={onRaw} startInBaseUnit />}
      </div>
      <div className="grid gap-1 text-sm sm:border-l sm:border-slate-100 sm:pl-3">
        <span className="text-slate-600">Потери при обработке</span>
        <span className="flex items-center gap-2">
          <NumberInput
            className={`input w-24 text-right tabular-nums ${lossOk ? '' : 'border-rose-400'}`}
            value={loss}
            onChange={onLoss}
          />
          <span className="text-slate-500">%</span>
          <span
            className="cursor-help text-slate-400"
            title="Очистка, уварка, обрезка, отжим — сколько процентов сырья не попадёт в готовую заготовку"
          >
            <CircleHelp size={16} />
          </span>
        </span>
      </div>
    </div>
  );
}

export function YieldCoefficient({
  mode, loss, raw, rawUnit,
}: { mode: YieldMode; loss: number; raw: number; rawUnit: string }) {
  const k = 100 - loss;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-950">
      <Info size={16} className="mt-0.5 shrink-0 text-indigo-500" />
      <div>
        <div>Коэффициент выхода: <b className="tabular-nums">{fmtPercent(k)}%</b></div>
        <div className="text-xs text-indigo-800">
          {loss === 0
            ? 'Без потерь: готовой заготовки получается столько же, сколько сырья.'
            : mode === 'output'
              ? `${fmtPercent(loss)}% потерь означают, что из 100% сырья получается ${fmtPercent(k)}% готового продукта.`
              : `Из ${fmtYield(raw)} ${rawUnit} сырья после потерь ${fmtPercent(loss)}% получается ${fmtPercent(k)}% готового продукта.`}
        </div>
      </div>
    </div>
  );
}

export function YieldResult({
  mode, net, raw, prepUnit, rawUnit, loss,
}: { mode: YieldMode; net: number; raw: number; prepUnit: string; rawUnit: string; loss: number }) {
  const byOutput = mode === 'output';
  const Icon = byOutput ? Scale : Droplet;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
      <Icon size={22} className="shrink-0 text-emerald-600" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-emerald-950">
          {byOutput ? 'Необходимое сырьё (брутто)' : 'Фактический выход готовой заготовки (нетто)'}
        </div>
        <div className="mt-0.5 text-xs text-emerald-800">
          {byOutput
            ? `Чтобы получить ${fmtYield(net)} ${prepUnit} готовой заготовки при потерях ${fmtPercent(loss)}%, нужно ${fmtYield(raw)} ${rawUnit} сырья.`
            : `${fmtYield(raw)} ${rawUnit} × ${fmtPercent(100 - loss)}% = ${fmtYield(net)} ${prepUnit} готового продукта.`}
        </div>
      </div>
      <div className="whitespace-nowrap text-xl font-semibold tabular-nums text-emerald-950">
        {byOutput ? `${fmtYield(raw)} ${rawUnit}` : `${fmtYield(net)} ${prepUnit}`}
      </div>
    </div>
  );
}

export function PrepCostPanel({
  mode, totalKop, perUnitKop, net, prepUnit, loss,
}: { mode: YieldMode; totalKop: number; perUnitKop: number; net: number; prepUnit: string; loss: number }) {
  const outputRow: [string, string] = ['Выход готовой заготовки (нетто)', `${fmtYield(net)} ${prepUnit}`];
  const kRow: [string, string] = ['Коэффициент выхода', `${fmtPercent(100 - loss)}%`];
  const rows: Array<[string, string]> = mode === 'output'
    ? [['Стоимость сырья (брутто)', formatRub(totalKop)], outputRow, kRow]
    : [
        [`Стоимость сырья (брутто) на ${fmtYield(net)} ${prepUnit}`, formatRub(totalKop)],
        [`Себестоимость 1 ${prepUnit} (с учётом потерь)`, formatRub(perUnitKop)],
        outputRow,
        kRow,
      ];
  return (
    <div className="rounded-lg border border-violet-100 bg-violet-50 px-4 py-3">
      <div className="flex items-center gap-3">
        <Database size={20} className="shrink-0 text-violet-600" />
        <div className="flex-1 font-medium text-violet-950">Себестоимость 1 {prepUnit} готовой заготовки</div>
        <div className="text-xl font-semibold tabular-nums text-violet-950">{formatRub(perUnitKop)}</div>
      </div>
      <dl className="mt-2 grid gap-1 pl-8 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3">
            <dt className="text-violet-800">{label}</dt>
            <dd className="tabular-nums text-violet-950">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
